import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Generic error definitions for Supabase database operations
 * (`supabase.from(...).select/insert/update/delete/upsert/...`).
 *
 * Covers PostgREST codes (PGRST*) and Postgres SQLSTATE codes that are
 * surfaced by `error.code` on a `PostgrestError`, plus client-side fallbacks.
 * Reuse this anywhere you talk to the Supabase DB — pass the offending code
 * to `getSupabaseDbError(code)`.
 */
export const SUPABASE_DB_ERRORS: Record<string, ErrorDefinition> = {
  // ---------------------------------------------------------
  // PostgREST codes
  // ---------------------------------------------------------
  PGRST116: {
    title: 'Record not found',
    message:
      "We couldn't find what you were looking for. We've been notified and are looking into it.",
    action: 'Please refresh and try again.',
    retryable: true,
    category: 'account',
  },

  PGRST204: {
    title: 'Save unavailable',
    message:
      "We couldn't save that right now — our records may be syncing. We've been notified and are looking into it.",
    action: 'Please try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  PGRST205: {
    title: 'Service unavailable',
    message:
      "We couldn't reach the right resource — our records may be syncing. We've been notified and are looking into it.",
    action: 'Please try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  PGRST301: {
    title: "Couldn't save your changes",
    message:
      "We didn't find a record to update — your session may be out of sync. We've been notified and are looking into it.",
    action: 'Please refresh and try once more.',
    retryable: true,
    category: 'account',
  },

  // ---------------------------------------------------------
  // Postgres SQLSTATE codes
  // ---------------------------------------------------------
  '23502': {
    title: 'Missing information',
    message: 'A required field was empty.',
    action: 'Please fill it in and try again.',
    retryable: true,
    category: 'validation',
  },

  '23505': {
    title: 'Already in use',
    message: 'That value is already linked to another record.',
    action: 'Please try a different one.',
    retryable: true,
    category: 'validation',
  },

  '23514': {
    title: 'Something looks off',
    message: "The information you entered doesn't look quite right.",
    action: 'Please review your entry and try again.',
    retryable: true,
    category: 'validation',
  },

  '22P02': {
    title: 'Invalid value',
    message: "We couldn't recognize one of the values you provided.",
    action: 'Please choose again from the options shown.',
    retryable: true,
    category: 'validation',
  },

  '42501': {
    title: "Couldn't save your changes",
    message:
      "Your session doesn't have permission to perform this action. We've been notified and are looking into it.",
    action: 'Please sign in again and try once more.',
    retryable: true,
    category: 'authentication',
  },

  '42703': {
    title: 'Save unavailable',
    message:
      "We couldn't save that field — our records may be syncing. We've been notified and are looking into it.",
    action: 'Please try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback codes
  // ---------------------------------------------------------
  db_update_failed: {
    title: "Couldn't save your changes",
    message: 'We had trouble saving just now.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  db_load_failed: {
    title: "Couldn't load your data",
    message: 'We had trouble loading those details.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'This action requires an internet connection.',
    action: 'Please connect to the internet and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a Supabase DB error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getSupabaseDbError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...SUPABASE_DB_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = SUPABASE_DB_ERRORS[code] ?? {
    title: "Couldn't save your changes",
    message:
      "An unexpected error occurred. We've been notified and are looking into it.",
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return {
    ...errorDef,
    code,
  };
}
